/**
 * Responsibility: choose which name ordering a table uses, from its header.
 *
 * It used to sit in a row above the table, which meant a control for one
 * column floating over all of them - and on a page with two tables, two of
 * them with nothing to say which was which. Every other sort control in
 * this app is the little arrow in its own column header, so this belongs
 * there too.
 *
 * **Why it is a span and not a button.** The kit renders a sortable header
 * as `<TableSortLabel>`, which is itself a button, and a button inside a
 * button is invalid HTML that React will complain about. A span with a
 * click handler nests legally, so that is what this is - and the handler
 * stops the event so choosing an ordering does not also re-sort the
 * column underneath it.
 *
 * **Always shown, rather than only while the name column is sorted.** The
 * kit's table keeps its sort state to itself and offers no callback, so
 * the page genuinely cannot know which column is active. Showing this
 * conditionally would mean guessing, and a control that flickers on a
 * guess is worse than one that is simply always there.
 */
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import type { SyntheticEvent } from 'react';
import type { NameSort } from './nameSort';

const NEXT: Record<NameSort, NameSort> = {
  alphabetical: 'number',
  number: 'alphabetical',
};

const SHOWN: Record<NameSort, string> = {
  alphabetical: 'A-Z',
  number: '#',
};

const MEANING: Record<NameSort, string> = {
  alphabetical: 'Sorted A to Z, ignoring the collector number. Click to sort by number.',
  number: 'Sorted by collector number, so a set reads in order. Click to sort A to Z.',
};

type NameSortHeaderProps = {
  /** The column's own word - "Name", "Card". */
  label: string;
  value: NameSort;
  onChange: (next: NameSort) => void;
};

export function NameSortHeader({ label, value, onChange }: NameSortHeaderProps) {
  const choose = (event: SyntheticEvent) => {
    // The header cell sorts on click. This is a different question asked
    // in the same place, so it must not also answer that one.
    event.preventDefault();
    event.stopPropagation();
    onChange(NEXT[value]);
  };

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" component="span">
      <span>{label}</span>
      <Tooltip title={MEANING[value]}>
        <Box
          component="span"
          role="button"
          tabIndex={0}
          aria-label="How to sort the name column"
          onClick={choose}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') choose(event);
          }}
          sx={{
            px: 0.6,
            borderRadius: 0.75,
            border: 1,
            borderColor: 'divider',
            fontSize: '0.68rem',
            lineHeight: 1.6,
            fontWeight: 600,
            color: 'text.secondary',
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
          }}
        >
          {SHOWN[value]}
        </Box>
      </Tooltip>
    </Stack>
  );
}
